import React, { useState, useEffect } from 'react';
import SendBatchModal from './SendBatchModal';
import { api } from '../services/api';
import './Phenix.css';

const Phenix = () => {
  const [phenixLogs, setPhenixLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSendBatchModal, setShowSendBatchModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'descending' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [tooltip, setTooltip] = useState({ visible: false, content: '', x: 0, y: 0 });

  useEffect(() => {
    fetchPhenixLogs();
  }, []);

  const fetchPhenixLogs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/firestore/get_all_documents', {
        params: {
          collection_name: 'phenix_log'
        }
      });
      
      const logs = response.data || [];
      setPhenixLogs(logs);
      setFilteredLogs(logs);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching phenix logs:', err);
      setError('Error fetching phenix logs');
      setLoading(false);
    }
  };

  useEffect(() => {
    let results = [...phenixLogs];

    // Filter by search term
    if (searchTerm) {
      results = results.filter(log => 
        log.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.assistant_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.response?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    results.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle timestamp sorting
      if (sortConfig.key === 'timestamp') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });

    setFilteredLogs(results);
  }, [phenixLogs, sortConfig, searchTerm]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'ascending' ? 'descending' : 'ascending'
    }));
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
    }
    return '';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString('fr-CA') + ' ' + date.toLocaleTimeString('fr-CA');
    } catch {
      return '-';
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const formatAssistantId = (assistantId) => {
    if (!assistantId) return '-';
    // If it matches the pattern asst_XXX_phenix_YYY, extract the middle part
    const match = assistantId.match(/^asst_(.+?)_phenix/);
    if (match && match[1]) {
      return match[1];
    }
    // Otherwise return truncated version
    return truncateText(assistantId, 30);
  };

  const anonymizePhone = (phone) => {
    if (!phone) return '-';
    const phoneStr = phone.toString().replace(/\D/g, ''); // Remove non-digits
    if (phoneStr.length < 8) return phone; // Too short to anonymize
    const first4 = phoneStr.substring(0, 4);
    const last4 = phoneStr.substring(phoneStr.length - 4);
    return `${first4}****${last4}`;
  };

  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case 'succeed':
      case 'success':
        return <span className="status-icon success" title="Success">✓</span>;
      case 'fail':
      case 'failed':
      case 'error':
        return <span className="status-icon error" title="Failed">✗</span>;
      case 'created':
      case 'in_progress':
      case 'pending':
        return <span className="status-icon pending" title="In Progress">⟳</span>;
      default:
        return <span className="status-icon unknown" title="Unknown">?</span>;
    }
  };

  const handleMouseEnter = (content, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      content: content,
      x: rect.left,
      y: rect.bottom + 5
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, content: '', x: 0, y: 0 });
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="phenix-container">
      <h2>Phénix</h2>
      
      <div className="phenix-controls">
        <div className="search-section">
          <input
            type="text"
            placeholder="Search by phone, assistant ID, or response..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <button
          onClick={() => setShowSendBatchModal(true)}
          className="send-batch-button"
        >
          Send Batch
        </button>

        <button
          onClick={fetchPhenixLogs}
          className="refresh-button"
        >
          Refresh
        </button>
      </div>

      <div className="table-container">
        <table className="phenix-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('timestamp')} className="sortable">
                Date of Sent{getSortIndicator('timestamp')}
              </th>
              <th onClick={() => handleSort('assistant_id')} className="sortable">
                Assistant ID{getSortIndicator('assistant_id')}
              </th>
              <th>Context</th>
              <th onClick={() => handleSort('phone')} className="sortable">
                Phone{getSortIndicator('phone')}
              </th>
              <th>Response</th>
              <th onClick={() => handleSort('status')} className="sortable">
                Status{getSortIndicator('status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log, index) => (
              <tr key={log.id || index}>
                <td>{formatDate(log.timestamp)}</td>
                <td className="monospace">{formatAssistantId(log.assistant_id)}</td>
                <td>
                  <div 
                    className="tooltip-container"
                    onMouseEnter={log.lead_context && log.lead_context.length > 40 ? (e) => handleMouseEnter(log.lead_context, e) : undefined}
                    onMouseLeave={log.lead_context && log.lead_context.length > 40 ? handleMouseLeave : undefined}
                  >
                    <span className="truncated-text">
                      {truncateText(log.lead_context, 40)}
                      {log.lead_context && log.lead_context.length > 40 && ' 📄'}
                    </span>
                  </div>
                </td>
                <td>{anonymizePhone(log.phone)}</td>
                <td>
                  <div 
                    className="tooltip-container"
                    onMouseEnter={log.response && log.response.length > 40 ? (e) => handleMouseEnter(log.response, e) : undefined}
                    onMouseLeave={log.response && log.response.length > 40 ? handleMouseLeave : undefined}
                  >
                    <span className="truncated-text">
                      {truncateText(log.response, 40)}
                      {log.response && log.response.length > 40 && ' 📄'}
                    </span>
                  </div>
                </td>
                <td className="status-cell">
                  {getStatusIcon(log.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredLogs.length === 0 && (
          <div className="no-data">
            No phenix logs found
          </div>
        )}
      </div>

      <SendBatchModal
        isOpen={showSendBatchModal}
        onClose={() => setShowSendBatchModal(false)}
      />
      
      {tooltip.visible && (
        <div 
          className="tooltip-global"
          style={{
            position: 'fixed',
            left: `${Math.min(tooltip.x, window.innerWidth - 420)}px`,
            top: `${tooltip.y}px`,
            zIndex: 99999,
            backgroundColor: '#333',
            color: 'white',
            padding: '15px',
            borderRadius: '6px',
            fontSize: '12px',
            width: '400px',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            pointerEvents: 'none'
          }}
        >
          <pre style={{ 
            margin: 0, 
            color: 'white', 
            fontFamily: 'Courier New, monospace', 
            fontSize: '11px',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word'
          }}>
            {tooltip.content}
          </pre>
        </div>
      )}
    </div>
  );
};

export default Phenix;